import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { Router } from '@angular/router';

@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="modal-overlay" (click)="onClose()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <!-- Header -->
        <div class="modal-header">
          <h3>{{ 'QR.SCAN_TITLE' | translate }}</h3>
          <button class="close-btn" (click)="onClose()" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Scanner Container -->
        <div class="scanner-container">
          <div id="qr-reader" #qrReader></div>
          
          <!-- Scanning Indicator -->
          @if (isScanning) {
            <div class="scanning-indicator">
              <div class="scan-line"></div>
            </div>
          }

          <!-- Camera Permission Denied -->
          @if (cameraError) {
            <div class="error-state">
              <i class="fas fa-camera-slash"></i>
              <p>{{ cameraError }}</p>
              <button class="retry-btn" (click)="retryCamera()">
                {{ 'QR.RETRY_CAMERA' | translate }}
              </button>
            </div>
          }

          <!-- Success State -->
          @if (scanSuccess) {
            <div class="success-state">
              <i class="fas fa-check-circle"></i>
              <p>{{ 'QR.SCAN_SUCCESS' | translate }}</p>
            </div>
          }
        </div>

        <!-- Instructions -->
        @if (!cameraError && !scanSuccess) {
          <div class="instructions">
            <i class="fas fa-info-circle"></i>
            <p>{{ 'QR.SCAN_INSTRUCTIONS' | translate }}</p>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      padding: 20px;
    }

    .modal-content {
      background: white;
      border-radius: 16px;
      width: 100%;
      max-width: 500px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
      overflow: hidden;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px;
      border-bottom: 2px solid #f3f4f6;
      background: linear-gradient(135deg, #9e1c20 0%, #c62828 100%);
      color: white;
    }

    .modal-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }

    .close-btn {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: white;
      font-size: 18px;
      transition: all 0.2s;
    }

    .close-btn:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.05);
    }

    .scanner-container {
      position: relative;
      width: 100%;
      min-height: 300px;
      background: #000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    #qr-reader {
      width: 100%;
      border: none;
    }

    /* Override html5-qrcode default styles */
    :host ::ng-deep #qr-reader {
      border: none !important;
    }

    :host ::ng-deep #qr-reader__dashboard_section_csr {
      display: none !important;
    }

    :host ::ng-deep #qr-reader video {
      width: 100% !important;
      height: auto !important;
      border: none !important;
    }

    .scanning-indicator {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .scan-line {
      width: 80%;
      height: 2px;
      background: linear-gradient(90deg, transparent, #9e1c20, transparent);
      animation: scan 2s ease-in-out infinite;
    }

    @keyframes scan {
      0%, 100% { transform: translateY(-100px); opacity: 0; }
      50% { transform: translateY(0); opacity: 1; }
    }

    .error-state, .success-state {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.95);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      text-align: center;
    }

    .error-state i {
      font-size: 64px;
      color: #ef4444;
      margin-bottom: 16px;
    }

    .success-state i {
      font-size: 64px;
      color: #10b981;
      margin-bottom: 16px;
      animation: successPop 0.5s ease-out;
    }

    @keyframes successPop {
      0% { transform: scale(0); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }

    .error-state p, .success-state p {
      margin: 0 0 20px 0;
      font-size: 16px;
      color: #1f2937;
      line-height: 1.5;
    }

    .retry-btn {
      background: linear-gradient(135deg, #9e1c20 0%, #c62828 100%);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .retry-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(158, 28, 32, 0.3);
    }

    .instructions {
      padding: 20px;
      background: #f9fafb;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      border-top: 1px solid #e5e7eb;
    }

    .instructions i {
      color: #9e1c20;
      font-size: 20px;
      margin-top: 2px;
    }

    .instructions p {
      margin: 0;
      font-size: 14px;
      color: #6b7280;
      line-height: 1.6;
    }

    @media (max-width: 640px) {
      .modal-content {
        max-width: 100%;
        border-radius: 0;
      }

      .scanner-container {
        min-height: 250px;
      }
    }
  `]
})
export class QrScannerComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();
  @Output() scanned = new EventEmitter<string>();

  private html5QrCode: Html5Qrcode | null = null;
  isScanning = false;
  cameraError = '';
  scanSuccess = false;

  constructor(
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.startScanner();
  }

  ngOnDestroy() {
    this.stopScanner();
  }

  /**
   * Initialize and start the QR scanner
   */
  async startScanner() {
    try {
      this.html5QrCode = new Html5Qrcode('qr-reader');
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await this.html5QrCode.start(
        { facingMode: 'environment' }, // Use back camera
        config,
        this.onScanSuccess.bind(this),
        this.onScanFailure.bind(this)
      );

      this.isScanning = true;
      this.cameraError = '';
    } catch (err: any) {
      console.error('Error starting scanner:', err);
      
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission denied')) {
        this.cameraError = this.translate.instant('QR.CAMERA_PERMISSION_DENIED');
      } else if (err.name === 'NotFoundError') {
        this.cameraError = this.translate.instant('QR.NO_CAMERA_FOUND');
      } else {
        this.cameraError = this.translate.instant('QR.CAMERA_ERROR');
      }
      
      this.isScanning = false;
    }
  }

  /**
   * Stop the QR scanner
   */
  async stopScanner() {
    if (this.html5QrCode) {
      try {
        const state = this.html5QrCode.getState();
        if (state === Html5QrcodeScannerState.SCANNING) {
          await this.html5QrCode.stop();
        }
        this.html5QrCode.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    this.isScanning = false;
  }

  /**
   * Handle successful QR code scan
   */
  async onScanSuccess(decodedText: string, decodedResult: any) {
    console.log('QR Code scanned:', decodedText);
    
    this.scanSuccess = true;
    this.isScanning = false;
    
    // Stop the scanner
    await this.stopScanner();
    
    // Parse the URL and extract application number
    const applicationNo = this.extractApplicationNo(decodedText);
    
    if (applicationNo) {
      // Navigate to application detail
      setTimeout(() => {
        this.scanned.emit(applicationNo);
        this.router.navigate(['/application-detail', applicationNo]);
        this.onClose();
      }, 1000); // Show success state for 1 second
    } else {
      // Invalid QR code format
      this.cameraError = this.translate.instant('QR.INVALID_QR');
      this.scanSuccess = false;
    }
  }

  /**
   * Handle scan failure (not an error, just no QR code detected)
   */
  onScanFailure(error: any) {
    // This is called continuously when no QR code is in frame
    // We don't need to show an error for this
  }

  /**
   * Extract application number from QR URL
   * Expected formats:
   * - https://insee4udev.svengit.com/application-detail/{applicationNo}
   * - https://insee4uuat.svengit.com/application-detail/{applicationNo}
   * - https://insee4u.svengit.com/application-detail/{applicationNo}
   */
  extractApplicationNo(url: string): string | null {
    try {
      // Check if it's a valid URL
      const urlPattern = /^https?:\/\//;
      if (!urlPattern.test(url)) {
        return null;
      }

      // Extract application number from URL
      const match = url.match(/\/application-detail\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (err) {
      console.error('Error parsing QR URL:', err);
      return null;
    }
  }

  /**
   * Retry camera access
   */
  async retryCamera() {
    this.cameraError = '';
    this.scanSuccess = false;
    await this.startScanner();
  }

  /**
   * Close the scanner modal
   */
  onClose() {
    this.stopScanner();
    this.closed.emit();
  }
}
